import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useRef,
  useState
} from "react";
import { MapReservationModal } from "./MapReservationModal";
import { MapModalParams } from "./types";

interface MapModalContextType {
  isOpen: boolean;
  openModal: (params: MapModalParams) => void;
  closeModal: () => void;
}

const MapModalContext = createContext<MapModalContextType | undefined>(
  undefined
);

export const useMapModalContext = () => {
  const context = useContext(MapModalContext);
  if (!context) {
    throw new Error(
      "useMapModalContext must be used within a MapModalProvider"
    );
  }
  return context;
};

export const MapModalProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [params, setParams] = useState<MapModalParams>({});
  const initialRouteRef = useRef<string | null>(null);
  const paramsRef = useRef<MapModalParams>({});

  // Keep paramsRef in sync with params
  paramsRef.current = params;

  const openModal = useCallback((newParams: MapModalParams) => {
    // Save the current route if not already saved
    if (!initialRouteRef.current) {
      // Save the full URL including query params
      initialRouteRef.current =
        window.location.pathname + window.location.search;
    }
    setParams(newParams);
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    paramsRef.current.onFinished?.();
    setIsOpen(false);
    setParams({});

    // Return to the initial route
    if (initialRouteRef.current) {
      window.history.replaceState(null, "", initialRouteRef.current);
      initialRouteRef.current = null;
    }
  }, []);

  return (
    <MapModalContext.Provider value={{ isOpen, openModal, closeModal }}>
      {children}
      <MapReservationModal
        isOpen={isOpen}
        onSuccess={closeModal}
        onClose={closeModal}
        params={params}
      />
    </MapModalContext.Provider>
  );
};
